import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { forceUnlockAll, getLockCount } from '../../hooks/useBodyScrollLock';

/**
 * RouteScrollGuard
 * ────────────────
 * Mount once at the top of the React tree (inside <Router>). Every time the
 * pathname changes, we wait ~350ms for any exit animations to finish, then
 * verify that NO real modal is still in the DOM. If a stale lock is detected
 * (lockCount > 0 with no matching dialog), we forcibly release it and scrub
 * any inline body styles. This is the safety-net for back navigation that
 * unmounts a drawer without the MutationObserver firing reliably.
 */
const RouteScrollGuard = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    const id = setTimeout(() => {
      try {
        const real = document.querySelectorAll(
          '[role="dialog"]:not([aria-hidden="true"]), [data-modal="open"], [aria-modal="true"]'
        );
        const stillOpen = Array.from(real).some((el) =>
          !(el.dataset && el.dataset.noScrollLock === 'true') &&
          el.offsetParent !== null
        );

        // Conditions that mean "we have a stale lock":
        //  1) lockCount > 0 but no real modal is open, OR
        //  2) body has a stuck position:fixed/overflow:hidden from earlier lock
        const body = document.body;
        const stuckBody = body.style.position === 'fixed' || body.style.overflow === 'hidden';

        if ((!stillOpen) && (getLockCount() > 0 || stuckBody)) {
          forceUnlockAll();
        }
      } catch (_) { /* noop */ }
    }, 350);

    return () => clearTimeout(id);
  }, [pathname]);

  return null;
};

export default RouteScrollGuard;
